import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Sparkles, Kanban, Sliders, LogOut, Crosshair, Menu, X, Compass, Bot, CreditCard, Megaphone, Factory, Handshake, Share2, Shirt, Boxes, Flame, LayoutTemplate, Users, GraduationCap, Clapperboard, Wand2 } from "lucide-react";
import { InstallButtons, MobileInstallBar } from "@/components/InstallButtons";
import { RepairBanner } from "@/components/AutopilotPanel";
import { useAuth } from "@/context/AuthContext";
import { TIER_LABELS } from "@/lib/api";
import { LangSwitcher } from "@/components/LangSwitcher";
import { UpgradeBanner } from "@/components/Upgrade";

const NAV = [
  { to: "/", label: "Genel Bakış", icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/pricing", label: "Paketler", icon: CreditCard, id: "nav-pricing" },
  { to: "/profile", label: "Profil & Tercihler", icon: Sliders, id: "nav-profile" },
  { to: "/methods", label: "Kazanç Yolları", icon: Compass, id: "nav-methods" },
  { to: "/opportunities", label: "Tüm Fırsatlar", icon: Sparkles, id: "nav-opportunities" },
  { to: "/automation", label: "Otomasyon", icon: Bot, id: "nav-automation" },
  { to: "/trends", label: "Trend Avcısı", icon: Flame, id: "nav-trends" },
  { to: "/factory", label: "Ürün Fabrikası", icon: Factory, id: "nav-factory" },
  { to: "/pod", label: "Tasarım Fabrikası", icon: Shirt, id: "nav-pod" },
  { to: "/saas", label: "SaaS Fabrikası", icon: Boxes, id: "nav-saas" },
  { to: "/templates", label: "Şablon Fabrikası", icon: LayoutTemplate, id: "nav-templates" },
  { to: "/courses", label: "Kurs Fabrikası", icon: GraduationCap, id: "nav-courses" },
  { to: "/shorts", label: "Video Fabrikası", icon: Clapperboard, id: "nav-shorts" },
  { to: "/mini", label: "Mini Fabrikalar", icon: Wand2, id: "nav-mini" },
  { to: "/marketing", label: "Pazarlama", icon: Megaphone, id: "nav-marketing" },
  { to: "/affiliate-outreach", label: "Affiliate Merkezi", icon: Share2, id: "nav-affiliate-hub" },
  { to: "/affiliate", label: "Gold Ortaklık", icon: Handshake, id: "nav-affiliate" },
  { to: "/tracking", label: "Takip Panosu", icon: Kanban, id: "nav-tracking" },
  { to: "/members", label: "Üyeler", icon: Users, id: "nav-members", admin: true },
];

function SidebarContent({ onNavigate }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => { await logout(); navigate("/login"); };
  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex items-center gap-3 px-5 pt-6 pb-4">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500/15 border border-emerald-500/30">
          <Crosshair className="h-5 w-5 text-emerald-400" />
        </div>
        <div>
          <div className="font-heading text-lg font-bold leading-none">Fırsat Avcısı</div>
          <div className="text-[11px] text-muted-foreground mt-1">Günde 1 saat · En iyi 3</div>
        </div>
      </div>
      <div className="mx-3 mb-3 rounded-xl border border-border/60 bg-white/[0.03] p-3" data-testid="sidebar-account">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 shrink-0 rounded-full bg-secondary grid place-items-center font-heading font-bold text-emerald-300 overflow-hidden">
            {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name?.[0] || "?").toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="truncate text-[11px] text-muted-foreground">{user?.email}</div>
          </div>
          <button onClick={handleLogout} data-testid="logout-button" title="Çıkış" className="rounded-md p-2 text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 transition-colors">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-2 flex items-center gap-2"><LangSwitcher /><span className="text-[11px] text-muted-foreground" data-testid="sidebar-tier">{TIER_LABELS[user?.plan?.status === "active" ? user?.plan?.tier : "free"] || "Ücretsiz"}</span></div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 pb-3 space-y-1" data-testid="sidebar-navigation">
        {NAV.filter((n) => !n.admin || user?.is_admin).map(({ to, label, icon: Icon, id }) => (
          <NavLink key={to} to={to} end={to === "/"} onClick={onNavigate} data-testid={id}
            className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-emerald-500/12 text-emerald-300 border border-emerald-500/20" : "text-slate-400 hover:text-slate-100 hover:bg-white/5 border border-transparent"}`}>
            <Icon className="h-4 w-4" /> {label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border/60 px-3 py-3"><InstallButtons compact /><p className="mt-2 text-center text-[10px] text-slate-600"><Link to="/impressum" className="hover:text-slate-400" data-testid="sidebar-impressum">Impressum</Link> · <Link to="/privacy" className="hover:text-slate-400">Gizlilik</Link> · <Link to="/terms" className="hover:text-slate-400">Şartlar</Link></p></div>
    </div>
  );
}

export function Layout({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen grain">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-border/60 bg-[#0D111A]/90 backdrop-blur-xl z-20">
        <SidebarContent />
      </aside>
      <div className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b border-border/60 bg-[#0D111A]/90 backdrop-blur-xl px-4 py-3">
        <div className="flex items-center gap-2 font-heading font-bold"><Crosshair className="h-5 w-5 text-emerald-400" /> Fırsat Avcısı</div>
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-button" className="p-2 rounded-md hover:bg-white/5"><Menu className="h-5 w-5" /></button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <aside className="absolute inset-y-0 left-0 w-72 bg-[#0D111A] border-r border-border overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOpen(false)} className="absolute right-3 top-4 p-2 text-slate-400"><X className="h-5 w-5" /></button>
            <SidebarContent onNavigate={() => setOpen(false)} />
          </aside>
        </div>
      )}
      <main className="relative z-10 lg:pl-64 pb-20 lg:pb-0">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10 py-8 lg:py-12"><UpgradeBanner /><RepairBanner />{children}</div>
      </main>
      <MobileInstallBar />
    </div>
  );
}
